import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject } from "rxjs";
import { map } from "rxjs/operators";
import { PublicClientApplication } from "@azure/msal-browser";
import { StorageService } from "./helpers/storage.service";
import { AlertService } from "./alert/alert.service";
import { AuthService } from "./auth/auth.service";
import { environment } from "../../environments/environment";
import { LoginBody } from "../pages/shared/models/requests/loginRequest";
import { OutlookResponse } from "../pages/shared/models/requests/verifyoutlookUser";

export interface MsalUserProfile {
	'@odata.context': string;
	businessPhones: string[];
	displayName: string;
	givenName: string;
	jobTitle: string;
	mail: string;
	mobilePhone: string | null;
	officeLocation: string | null;
	preferredLanguage: string | null;
	surname: string;
	userPrincipalName: string;
	id: string;
}


@Injectable({
	providedIn: "root",
})
export class MsalService {
	//Upgraded MSAL
	private pca: PublicClientApplication;
	private isInitialized = false;
	private initializePromise: Promise<void> | null = null;
	userData: any;
	allowedServices: any;

	// For Loading status
	private loadingSource = new BehaviorSubject<boolean>(false);
	loadingStatus = this.loadingSource.asObservable();

	constructor(
		private alertService: AlertService,
		private storage: StorageService,
		private _authService: AuthService,
		public httpClient: HttpClient,
		private router: Router,
	) {
		const applicationConfig = {
			auth: {
				clientId: environment.production ?
					"89b07839-760e-4ae1-9448-29a8db0bb343" :
					"80239adc-7021-47b0-b9c6-d0c0a3342325",
				authority: "https://login.microsoftonline.com/94d0b6f7-6fe5-47d9-910f-4e0e60075875",
				redirectUri: window.location.origin,
			},
			cache: {
				cacheLocation: "sessionStorage",
				storeAuthStateInCookie: true,
			}
		};
		this.pca = new PublicClientApplication(applicationConfig);
		this.ensureInitialized().catch((err) => {
			console.error("[MSAL Service] Constructor initialization failed:", err);
		});
	}

	private async ensureInitialized(): Promise<void> {
		if (this.isInitialized) {
			return;
		}
		if (!this.initializePromise) {
			this.initializePromise = (async () => {
				await this.pca.initialize();
				try {
					const result = await this.pca.handleRedirectPromise();
					if (result && result.account) {
						this.pca.setActiveAccount(result.account);
						// Recover the activity that was saved before the redirect
						const activity = sessionStorage.getItem('msal_redirect_activity');
						sessionStorage.removeItem('msal_redirect_activity');
						if (activity) {
							this.updateLoadingStatus(true);
							// Use the access token from the redirect result directly if available,
							// otherwise fall back to acquiring silently.
							if (result.accessToken) {
								this.getAccountDetails(result.accessToken, activity);
							} else {
								this.getAccessTokenFromOutlook({ scopes: ["user.read", "mail.send"] }, activity);
							}
						}
					}
				} catch (err: any) {
					console.error("[MSAL Service] handleRedirectPromise failed:", err);
					sessionStorage.removeItem('msal_redirect_activity');
					this.alertService.error(err.message || 'Microsoft authentication failed');
					this.updateLoadingStatus(false);
				}
				this.isInitialized = true;
			})();
		}
		return this.initializePromise;
	}

	updateLoadingStatus(val: boolean) {
		this.loadingSource.next(val);
	}

	async microsoftSignIn(activity: string) {
		this.updateLoadingStatus(true);
		try {
			await this.ensureInitialized();
			// Persist activity before navigating away — the page is replaced by Microsoft's login page
			sessionStorage.setItem('msal_redirect_activity', activity);
			await this.pca.loginRedirect({
				scopes: ["user.read", "mail.send"]
			});
			// Execution stops here — the browser navigates to Microsoft's login page.
			// When it comes back, handleRedirectPromise() in ensureInitialized() picks up
			// the result and calls getAccountDetails() with the saved activity.
		} catch (err: any) {
			console.error("[MSAL Service] MSAL loginRedirect Error:", err);
			sessionStorage.removeItem('msal_redirect_activity');
			this.alertService.error(err.message || 'Microsoft authentication failed');
			this.updateLoadingStatus(false);
		}
	}


	async getAccessTokenFromOutlook(loginRequest: any, activity: string) {
		try {
			await this.ensureInitialized();
			const account = this.pca.getActiveAccount() || this.pca.getAllAccounts()[0];
			const silentRequest = {
				...loginRequest,
				account: account
			};
			const response = await this.pca.acquireTokenSilent(silentRequest);
			this.getAccountDetails(response.accessToken, activity);
		} catch (err: any) {
			// Fall back to redirect if interaction is required
			if (err.name === "InteractionRequiredAuthError" || err.errorCode === "token_renewal_error" || err.errorCode === "consent_required" || err.errorCode === "interaction_required") {
				try {
					sessionStorage.setItem('msal_redirect_activity', activity);
					await this.pca.acquireTokenRedirect(loginRequest);
					// Page navigates away — result handled in ensureInitialized on return
				} catch (redirectErr: any) {
					console.error("[MSAL Service] acquireTokenRedirect Error:", redirectErr);
					sessionStorage.removeItem('msal_redirect_activity');
					this.alertService.error(redirectErr.message || 'Failed to acquire access token');
					this.updateLoadingStatus(false);
				}
			} else {
				console.error("[MSAL Service] MSAL acquireTokenSilent Error:", err);
				this.alertService.error(err.message || 'Silent token acquisition failed');
				this.updateLoadingStatus(false);
			}
		}
	}



	getAccountDetails(token, activity) {
		this.storage.setOutlookToken(token)
		const graphEndpoint = "https://graph.microsoft.com/v1.0/me";
		this.callMicrosoftAPI(graphEndpoint).subscribe((resp) => {
			if (activity === 'sign-in') {
				this.applicationLogin(resp['mail'])
			}
			else if (activity === 'verification') {
				this.verifyUser(resp)
			}
		}, (error) => {
			this.updateLoadingStatus(false);
			this.alertService.error(error['statusText']);
		});
	}



	applicationLogin(mail) {
		const loginBody = new LoginBody();
		loginBody.username = mail;
		loginBody.password = mail;
		this._authService.login(loginBody).subscribe((resp) => {
			this.storage.storeCurrentUser(resp);
			this.alertService.success('Login was successful', true);
			this.router.navigate(["user/dashboard"]);
			this.updateLoadingStatus(false);
		}, (error) => {
			this.alertService.error(error);
			this.updateLoadingStatus(false);
		})
	}


	verifyUser(outlookResponse) {
		const verifyUserObject = new OutlookResponse();
		verifyUserObject.displayName = outlookResponse.displayName;
		verifyUserObject.jobTitle = outlookResponse.jobTitle;
		verifyUserObject.email = outlookResponse.mail;

		if (outlookResponse.businessPhones && outlookResponse.businessPhones.length > 0) {
			verifyUserObject.phone = outlookResponse.businessPhones[0].length > 11 ?
				"0" + (outlookResponse.businessPhones[0].split(","))[0].slice(4).replace(/\s+/g, ' ').trim() : outlookResponse.businessPhones[0];
		} else if (outlookResponse.mobilePhone) {
			verifyUserObject.phone = outlookResponse.mobilePhone;
		} else {
			verifyUserObject.phone = "";
		}

		this._authService.verifyUser(verifyUserObject).subscribe((resp) => {
			this.alertService.success('Invitation verified', true);
			this.router.navigate(["sign-in"]);
			this.updateLoadingStatus(false);
		}, (error) => {
			console.error("verifyUser Error Response:", error);
			this.alertService.error(error);
			this.updateLoadingStatus(false);
		})
	}

	callMicrosoftAPI(graphEndpoint) {
		//calls the outlook endpoint to get a user's profile , header is added in application's interceptor
		return this.httpClient.get(graphEndpoint).pipe(
			map((resp) => {
				return resp;
			})
		);
	}

	// ----------------------------------------- //
	async newMicrosoftSignIn(activity: 'sign-in' | 'verification' = 'sign-in') {
		this.updateLoadingStatus(true);
		try {
			await this.ensureInitialized();
			sessionStorage.setItem('msal_redirect_activity', activity);
			await this.pca.loginRedirect({
				scopes: ['user.read', 'mail.send']
			});
			// Page navigates away — result handled in ensureInitialized on return
		} catch (error: any) {
			console.error("[MSAL Service] newMicrosoftSignIn Error:", error);
			sessionStorage.removeItem('msal_redirect_activity');
			this.alertService.error(error.message || 'Microsoft sign-in failed');
			this.updateLoadingStatus(false);
		}
	}

}
